import GetLoading from "../../share/getLoading";
import "./MyDepartment.css";
// component/OrganisationChart.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Button,
  message,
  Modal,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Tree,
  Dropdown,
  Empty,
  Badge,
  Avatar,
} from "antd";
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ApartmentOutlined,
  MenuOutlined,
  ReloadOutlined,
  SwapOutlined,
  ShareAltOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CloudUploadOutlined,
  CloseOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { request } from "../../share/request";
import { linkPath } from "../../share/helper";
import DeleteDepartmentModal from "../../component/DeleteModal/DeleteDepartmentModal";
import DepartmentStaffModal from "./DepartmentStaffModal";
const { Option } = Select;
const { DirectoryTree } = Tree;

// Font styles
const khmerFont = {
  fontFamily: "'Content',  system-ui, sans-serif",
};

const englishFont = {
  fontFamily: "'Poppins-SemiBold', 'Poppins', 'Poppins SemiBold', system-ui, sans-serif",
};

const mixedFont = {
  fontFamily: "'Poppins-SemiBold', 'Content', system-ui, sans-serif",
};

const OrganisationChart = ({ org_code, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [subModalVisible, setSubModalVisible] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [newDepartment, setNewDepartment] = useState({
    code: "",
    name: "",
    name_en: "",
    parent_id: null,
    level_no: 1,
  });
  const [editDepartment, setEditDepartment] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [activeNodeKeys, setActiveNodeKeys] = useState([]);
  const [stats, setStats] = useState(null);
  const [nextCode, setNextCode] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const treeDataRef = useRef(treeData);
  // For delete confirmation
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Add this state
  const [staffModalVisible, setStaffModalVisible] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Add this function to open the modal
  const onOpenStaffModal = (item) => {
    setSelectedDepartment(item);
    console.log("Selected department for staff modal:", item);
    setStaffModalVisible(true);
  };

  // Keep ref in sync
  useEffect(() => {
    treeDataRef.current = treeData;
  }, [treeData]);

  // Load pending changes from localStorage on mount
  useEffect(() => {
    loadPendingChangesFromStorage();
  }, [org_code]);

  // Save pending changes to localStorage whenever they change
  useEffect(() => {
    savePendingChangesToStorage();
    setHasPendingChanges(pendingChanges.length > 0);
  }, [pendingChanges]);

  // Fetch departments
  useEffect(() => {
    fetchDepartments();
    fetchStats();
    getNextDepartmentCode();
  }, [org_code]);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const param = new URLSearchParams({ org_code: org_code });
      const res = await request("departmentall?" + param.toString(), "get", {});
      if (res && res.listdepartment) {
        setDepartments(res.listdepartment);
        console.log("Fetched departments:", res.listdepartment);
        const tree = buildTreeData(res.listdepartment);
        setTreeData(tree);
        const allKeys = getAllKeys(tree);
        setExpandedKeys(allKeys);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      message.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  // Load pending changes from localStorage
  const loadPendingChangesFromStorage = () => {
    try {
      const storageKey = `pending_changes_${org_code}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const changes = JSON.parse(saved);
        setPendingChanges(changes);
      }
    } catch (error) {
      console.error("Error loading pending changes:", error);
    }
  };

  // Save pending changes to localStorage
  const savePendingChangesToStorage = () => {
    try {
      const storageKey = `pending_changes_${org_code}`;
      if (pendingChanges.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(pendingChanges));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error("Error saving pending changes:", error);
    }
  };

  // Add pending change to queue
  const addPendingChange = (change) => {
    setPendingChanges(prev => {
      const filtered = prev.filter(c => c.code !== change.code);
      return [...filtered, { ...change, timestamp: Date.now() }];
    });
  };

  // Add multiple pending changes
  const addPendingChanges = (changes) => {
    setPendingChanges(prev => {
      let newChanges = [...prev];
      changes.forEach(change => {
        const filtered = newChanges.filter(c => c.code !== change.code);
        newChanges = [...filtered, { ...change, timestamp: Date.now() }];
      });
      return newChanges;
    });
  };

  // Clear all pending changes
  const clearPendingChanges = () => {
    setPendingChanges([]);
    localStorage.removeItem(`pending_changes_${org_code}`);
    setHasPendingChanges(false);
  };

  // Apply all pending changes to server
  const applyPendingChanges = async () => {
    // if (pendingChanges.length === 0) {
    //   message.info("No pending changes to apply");
    //   return;
    // }

    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const change of pendingChanges) {
        try {
          if (change.type === 'range') {
            await request("department/range", "put", {
              code: change.code,
              range_id: change.range_id
            });
            successCount++;
          } else if (change.type === 'move') {
            await request("department/move", "put", {
              code: change.code,
              new_parent_id: change.new_parent_id,
              org_code: org_code,
            });
            successCount++;
          }
        } catch (error) {
          console.error(`Error applying change for ${change.code}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        message.success(`Applied ${successCount} change(s) successfully`);
        // Clear pending changes from localStorage and state
        clearPendingChanges();
        // Refresh data from server to get updated state
        await fetchDepartments();
        await fetchStats();
        if (onSave) onSave();
      }

      if (errorCount > 0) {
        message.warning(`${errorCount} change(s) failed. Please try again.`);
      }
    } catch (error) {
      console.error("Error applying changes:", error);
      message.error("Failed to apply changes");
    } finally {
      setSaving(false);
    }
  };

  // Discard all pending changes
  const discardPendingChanges = () => {
    Modal.confirm({
      title: "Discard Changes",
      content: `You have ${pendingChanges.length} pending change(s). Are you sure you want to discard all of them?`,
      okText: "Yes, Discard",
      cancelText: "Cancel",
      onOk: async () => {
        clearPendingChanges();
        await fetchDepartments();
        message.success("Pending changes discarded");
      },
    });
  };

  const getNodeDot = (nodeKey, hasChildren) => {
    const isActive = activeNodeKeys && activeNodeKeys.indexOf(nodeKey) !== -1;
    if (hasChildren) {
      return (
        <div
          style={isActive ? styles.dotYellow : styles.dotGreen}
        />
      );
    }
    return <div style={styles.dotGray} />;
  };

  const getNextDepartmentCode = async () => {
    try {
      const res = await request(`department/next-code?org_code=${org_code}`, "get", {});
      if (res && res.success) {
        setNextCode(res.data.next_code);
      }
    } catch (error) {
      console.error("Error getting next code:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await request(`department/org-stats?org_code=${org_code}`, "get", {});
      if (res && res.success) {
        setStats(res.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const getAllKeys = (nodes) => {
    let keys = [];
    nodes.forEach(node => {
      keys.push(node.key);
      if (node.children && node.children.length > 0) {
        keys = [...keys, ...getAllKeys(node.children)];
      }
    });
    return keys;
  };

  // Flatten tree to find a node by code (more reliable)
  const findNodeInTree = (nodes, targetCode) => {
    for (const node of nodes) {
      if (String(node.code) === String(targetCode)) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = findNodeInTree(node.children, targetCode);
        if (found) return found;
      }
    }
    return null;
  };

  // Find node with parent and index
  const findNodeWithParentAndIndex = (nodes, targetCode, parent = null) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (String(node.code) === String(targetCode)) {
        return { node, parent, index: i, siblings: nodes };
      }
      if (node.children && node.children.length > 0) {
        const found = findNodeWithParentAndIndex(node.children, targetCode, node);
        if (found) return found;
      }
    }
    return null;
  };

  // Deep clone tree data
  const deepCloneTree = (nodes) => {
    return nodes.map(node => ({
      ...node,
      children: node.children ? deepCloneTree(node.children) : []
    }));
  };

  // Update local range orders and save to pending changes
  const updateLocalRangeOrders = (siblings) => {
    const updates = [];
    for (let i = 0; i < siblings.length; i++) {
      const newRangeId = i + 1;
      if (siblings[i].range_id !== newRangeId) {
        updates.push({
          type: 'range',
          code: siblings[i].code,
          range_id: newRangeId,
          old_range_id: siblings[i].range_id,
          department_name: siblings[i].department_name,
        });
        siblings[i].range_id = newRangeId;
      }
    }
    return updates;
  };

  // Move node up within its parent's children list
  const moveNodeUp = useCallback(async (nodeCode) => {
    if (isMoving) {
      message.warning("Please wait, moving in progress...");
      return;
    }

    setIsMoving(true);

    try {
      const currentTree = treeDataRef.current;

      if (!currentTree || currentTree.length === 0) {
        message.error("Tree data is empty. Please refresh.");
        setIsMoving(false);
        return;
      }

      const nodeExists = findNodeInTree(currentTree, nodeCode);
      if (!nodeExists) {
        console.error("Node not found:", nodeCode);
        message.error(`Node ${nodeCode} not found. Please refresh.`);
        setIsMoving(false);
        return;
      }

      const result = findNodeWithParentAndIndex(currentTree, nodeCode);
      if (!result) {
        message.error("Node position not found. Please refresh.");
        setIsMoving(false);
        return;
      }

      const { parent, index, siblings } = result;

      if (index > 0) {
        const newTreeData = deepCloneTree(currentTree);

        let newSiblings;

        if (parent) {
          const parentResult = findNodeWithParentAndIndex(newTreeData, parent.code);
          if (parentResult) {
            newSiblings = parentResult.node.children;
          }
        } else {
          newSiblings = newTreeData;
        }

        if (!newSiblings) {
          message.error("Could not find siblings. Please refresh.");
          setIsMoving(false);
          return;
        }

        // Swap positions
        const temp = newSiblings[index];
        newSiblings[index] = newSiblings[index - 1];
        newSiblings[index - 1] = temp;

        setTreeData(newTreeData);

        const updates = updateLocalRangeOrders(newSiblings);
        if (updates.length > 0) {
          addPendingChanges(updates);
          message.success(`Moved up successfully. ${updates.length} change(s) pending.`);
        } else {
          message.success("Moved up successfully");
        }
      } else {
        message.warning("Already at the top");
      }
    } catch (error) {
      console.error("Error moving node up:", error);
      message.error("Failed to move node: " + error.message);
    } finally {
      setIsMoving(false);
    }
  }, [isMoving]);

  // Move node down within its parent's children list
  const moveNodeDown = useCallback(async (nodeCode) => {
    if (isMoving) {
      message.warning("Please wait, moving in progress...");
      return;
    }

    setIsMoving(true);

    try {
      const currentTree = treeDataRef.current;

      if (!currentTree || currentTree.length === 0) {
        message.error("Tree data is empty. Please refresh.");
        setIsMoving(false);
        return;
      }

      const nodeExists = findNodeInTree(currentTree, nodeCode);
      if (!nodeExists) {
        console.error("Node not found:", nodeCode);
        message.error(`Node ${nodeCode} not found. Please refresh.`);
        setIsMoving(false);
        return;
      }

      const result = findNodeWithParentAndIndex(currentTree, nodeCode);
      if (!result) {
        message.error("Node position not found. Please refresh.");
        setIsMoving(false);
        return;
      }

      const { parent, index, siblings } = result;

      if (index < siblings.length - 1) {
        const newTreeData = deepCloneTree(currentTree);

        let newSiblings;

        if (parent) {
          const parentResult = findNodeWithParentAndIndex(newTreeData, parent.code);
          if (parentResult) {
            newSiblings = parentResult.node.children;
          }
        } else {
          newSiblings = newTreeData;
        }

        if (!newSiblings) {
          message.error("Could not find siblings. Please refresh.");
          setIsMoving(false);
          return;
        }

        // Swap positions
        const temp = newSiblings[index];
        newSiblings[index] = newSiblings[index + 1];
        newSiblings[index + 1] = temp;

        setTreeData(newTreeData);

        const updates = updateLocalRangeOrders(newSiblings);
        if (updates.length > 0) {
          addPendingChanges(updates);
          message.success(`Moved down successfully. ${updates.length} change(s) pending.`);
        } else {
          message.success("Moved down successfully");
        }
      } else {
        message.warning("Already at the bottom");
      }
    } catch (error) {
      console.error("Error moving node down:", error);
      message.error("Failed to move node: " + error.message);
    } finally {
      setIsMoving(false);
    }
  }, [isMoving]);

  // Build tree data with proper sorting by range_id
  const buildTreeData = (depts) => {
    const map = new Map();
    const roots = [];

    const sortedDepts = [...depts].sort((a, b) => (a.range_id || 999) - (b.range_id || 999));

    sortedDepts.forEach(dept => {
      const hasChildren = depts.some(d => d.parent_id && String(d.parent_id) === String(dept.code));
      const hasPending = pendingChanges.some(p => p.code === dept.code && p.type === 'range');

      map.set(dept.code, {
        title: (
          <div style={styles.treeNodeTitle}>
            <div style={styles.treeNodeIcon}>
              {getNodeDot(dept.code, hasChildren)}
              <ApartmentOutlined style={{ color: "#1890ff", fontSize: 20 }} />
            </div>
            <div style={styles.treeNodeInfo}>
              <div style={styles.treeNodeName}>
                <strong style={khmerFont}>{dept.department_name}</strong>
                {dept.department_en && (
                  <span style={{ ...styles.treeNodeEn, ...englishFont }}>
                    ({dept.department_en})
                  </span>
                )}

              </div>
              <div style={styles.staffContainer}>
                {dept.staff_list && dept.staff_list.map((staff, index) => (
                  <Tooltip key={index} title={`${staff.fullname || staff.username}${staff.rank_short ? ` (${staff.rank_short})` : ''}`}>
                    <div style={styles.avatarWrapper}>
                      <Avatar
                        src={staff.image ? `${linkPath}${staff.image}` : null}
                        icon={!staff.image && <UserOutlined />}
                        style={styles.staffAvatar}
                        size="medium"
                      >
                        {!staff.image && staff.fullname ? staff.fullname.charAt(0).toUpperCase() : null}
                      </Avatar>

                      <span style={styles.onlineDot}></span>

                    </div>
                  </Tooltip>
                ))}
              </div>
              {/* <div style={styles.treeNodeMeta}>
                <Tag color="blue" style={styles.metaTag}>
                  Code: {dept.code}
                </Tag>
                <Tag color="cyan" style={styles.metaTag}>
                  Level: {dept.level_no}
                </Tag>
                {dept.range_id && (
                  <Tag color="green" style={styles.metaTag}>
                    Order: {dept.range_id}
                  </Tag>
                )}
                {dept.status === 1 ? (
                  <Tag color="success" style={styles.metaTag}>
                    Active
                  </Tag>
                ) : (
                  <Tag color="error" style={styles.metaTag}>
                    Inactive
                  </Tag>
                )}
              </div> */}
            </div>
            <div style={styles.treeNodeActions}>
              <Space size={2}>
                <Tooltip title="View Staff">
                  <Button
                    type="default"
                    icon={<TeamOutlined />}
                    onClick={() => onOpenStaffModal(dept)}
                    title="View Staff"
                  />
                </Tooltip>
                <Tooltip title="Move Up">
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveNodeUp(dept.code);
                    }}
                    disabled={isMoving}
                  />
                </Tooltip>
                <Tooltip title="Move Down">
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveNodeDown(dept.code);
                    }}
                    disabled={isMoving}
                  />
                </Tooltip>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "add",
                        label: "បន្ថែមផ្នែករង",
                        icon: <PlusOutlined />,
                        onClick: () => openAddSubModal(dept),
                      },
                      {
                        key: "edit",
                        label: "កែប្រែ",
                        icon: <EditOutlined />,
                        onClick: () => openEditModal(dept),
                      },
                      {
                        key: "move",
                        label: "ផ្លាស់ទី",
                        icon: <SwapOutlined />,
                        onClick: () => openMoveModal(dept),
                      },
                      {
                        type: "divider",
                      },
                      {
                        key: "delete",
                        label: "លុប",
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => openDeleteModal(dept),
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <Button type="text" size="small" icon={<MenuOutlined />} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              </Space>
            </div>
          </div>
        ),
        key: dept.code,
        code: dept.code,
        department_name: dept.department_name,
        department_en: dept.department_en,
        level_no: dept.level_no,
        range_id: dept.range_id,
        status: dept.status,
        parent_id: dept.parent_id,
        children: [],
      });
    });

    sortedDepts.forEach(dept => {
      const node = map.get(dept.code);
      if (dept.parent_id && dept.parent_id !== 0 && dept.parent_id !== "0" && map.has(dept.parent_id)) {
        const parent = map.get(dept.parent_id);
        if (parent) {
          parent.children.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    const sortChildren = (nodes) => {
      nodes.sort((a, b) => (a.range_id || 999) - (b.range_id || 999));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortChildren(node.children);
        }
      });
    };
    sortChildren(roots);

    return roots;
  };

  const openAddSubModal = (parentNode) => {
    setSelectedNode(parentNode);
    setNewDepartment({
      code: nextCode || "",
      name: "",
      name_en: "",
      parent_id: parentNode.code,
      level_no: (parentNode.level_no || 0) + 1,
    });
    setSubModalVisible(true);
  };

  const openEditModal = (node) => {
    setEditDepartment({ ...node });
    setEditModalVisible(true);
  };

  const openMoveModal = (node) => {
    setSelectedNode(node);
    setMoveTarget(node.parent_id || null);
    setMoveModalVisible(true);
  };

  const handleAddSubDepartment = async () => {
    if (!newDepartment.name) {
      message.warning("សូមបញ្ចូលឈ្មោះផ្នែក");
      return;
    }

    try {
      const getMaxRangeId = () => {
        const findParentAndChildren = (nodes) => {
          for (let node of nodes) {
            if (String(node.code) === String(newDepartment.parent_id)) {
              const maxRange = Math.max(...node.children.map(c => c.range_id || 0), 0);
              return maxRange + 1;
            }
            if (node.children && node.children.length > 0) {
              const result = findParentAndChildren(node.children);
              if (result) return result;
            }
          }
          return 1;
        };
        return findParentAndChildren(treeData);
      };

      const newRangeId = getMaxRangeId();

      const data = {
        code: newDepartment.code,
        department_name: newDepartment.name,
        department_en: newDepartment.name_en,
        parent_id: newDepartment.parent_id,
        level_no: newDepartment.level_no,
        range_id: newRangeId,
        status: 1,
        org_code: org_code,
      };
      const res = await request("department", "post", data);
      if (res && res.success) {
        message.success("បានបន្ថែមផ្នែករងដោយជោគជ័យ");
        setSubModalVisible(false);
        await fetchDepartments();
        await fetchStats();
        await getNextDepartmentCode();
        setNewDepartment({
          code: "",
          name: "",
          name_en: "",
          parent_id: null,
          level_no: 1,
        });
      } else {
        message.error(res?.message || "Failed to add department");
      }
    } catch (error) {
      console.error("Error adding department:", error);
      message.error("Failed to add department");
    }
  };

  const handleUpdateDepartment = async () => {
    if (!editDepartment) return;

    try {
      const data = {
        code: editDepartment.code,
        department_name: editDepartment.department_name,
        department_en: editDepartment.department_en,
        level_no: editDepartment.level_no,
        status: editDepartment.status,
        parent_id: editDepartment.parent_id === 0 ? null : editDepartment.parent_id,
        range_id: editDepartment.range_id,
      };
      const res = await request("department", "put", data);
      if (res && res.success) {
        message.success("បានកែប្រែផ្នែកដោយជោគជ័យ");
        setEditModalVisible(false);
        await fetchDepartments();
        await fetchStats();
      } else {
        message.error(res?.message || "Failed to update department");
      }
    } catch (error) {
      console.error("Error updating department:", error);
      message.error("Failed to update department");
    }
  };

  const handleMoveDepartment = async () => {
    if (!selectedNode) return;

    addPendingChange({
      type: 'move',
      code: selectedNode.code,
      new_parent_id: moveTarget === 0 ? null : moveTarget,
      old_parent_id: selectedNode.parent_id,
      department_name: selectedNode.department_name,
    });

    setMoveModalVisible(false);
    message.success("Move added to pending changes. Click 'Apply Changes' to save to server.");

    await fetchDepartments();
  };

  const handleDeleteDepartment = async (node) => {
    Modal.confirm({
      title: "បញ្ជាក់ការលុប",
      content: `តើអ្នកពិតជាចង់លុប ${node.department_name} មែនទេ?`,
      okText: "លុប",
      cancelText: "បោះបង់",
      okType: "danger",
      onOk: async () => {
        try {
          const res = await request("department/" + node.code, "delete", {});
          if (res && res.success) {
            message.success("បានលុបផ្នែកដោយជោគជ័យ");
            await fetchDepartments();
            await fetchStats();
            await getNextDepartmentCode();
          } else {
            message.error(res?.message || "Failed to delete department");
          }
        } catch (error) {
          console.error("Error deleting department:", error);
          message.error("Failed to delete department");
        }
      },
    });
  };
  // Open delete modal
  // Open delete modal
  const openDeleteModal = (dept) => {
    // Check if department has staff
    if (dept.staff_list && dept.staff_list.length > 0) {
      Modal.warning({
        title: "Cannot Delete Department",
        content: (
          <div>
            <p>This department has <strong>{dept.staff_list.length}</strong> staff member(s).</p>
            <p>Please reassign or remove all staff before deleting this department.</p>
            <div style={{ marginTop: 12 }}>
              <strong>Staff in this department:</strong>
              <ul style={{ marginTop: 8 }}>
                {dept.staff_list.slice(0, 5).map((staff, idx) => (
                  <li key={idx}>{staff.fullname || staff.username}</li>
                ))}
                {dept.staff_list.length > 5 && (
                  <li>... and {dept.staff_list.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        ),
        okText: "OK",
      });
      return;
    }

    // Check if department has children (sub-departments)
    // Method 1: Check in the treeData if this department has any children nodes
    const findNodeInTreeByCode = (nodes, targetCode) => {
      for (const node of nodes) {
        if (String(node.code) === String(targetCode)) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeInTreeByCode(node.children, targetCode);
          if (found) return found;
        }
      }
      return null;
    };

    const departmentNode = findNodeInTreeByCode(treeDataRef.current, dept.code);
    const hasChildren = departmentNode && departmentNode.children && departmentNode.children.length > 0;

    if (hasChildren) {
      Modal.warning({
        title: "Cannot Delete Department",
        content: (
          <div>
            <p>This department has <strong>{departmentNode.children.length}</strong> sub-department(s).</p>
            <p>Please delete or reassign all sub-departments before deleting this department.</p>
            <div style={{ marginTop: 12 }}>
              <strong>Sub-departments:</strong>
              <ul style={{ marginTop: 8 }}>
                {departmentNode.children.slice(0, 5).map((child, idx) => (
                  <li key={idx}>{child.department_name} ({child.code})</li>
                ))}
                {departmentNode.children.length > 5 && (
                  <li>... and {departmentNode.children.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        ),
        okText: "OK",
      });
      return;
    }

    setDepartmentToDelete(dept);
    setDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!departmentToDelete) return;

    setDeleteLoading(true);
    try {
      const res = await request("department/" + departmentToDelete.code, "delete", {});
      if (res && res.success) {
        message.success(`Department "${departmentToDelete.department_name}" deleted successfully`);
        setDeleteModalVisible(false);
        setDepartmentToDelete(null);
        await fetchDepartments();
        await fetchStats();
        await getNextDepartmentCode();
        if (onSave) onSave();
      } else {
        message.error(res?.message || "Failed to delete department");
      }
    } catch (error) {
      console.error("Error deleting department:", error);
      message.error("Failed to delete department");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRefresh = async () => {
    clearPendingChanges();
    await fetchDepartments();
    await fetchStats();
    await getNextDepartmentCode();
    message.success("Refreshed successfully");
  };

  const onSelect = (keys, info) => {
    setSelectedKeys(keys);
    setActiveNodeKeys(keys);
    if (info.node) {
      setSelectedNode({
        code: info.node.key,
        department_name: info.node.department_name,
        parent_id: info.node.parent_id,
      });
    }
  };

  if (loading) {
    return (
      <div className="department-fullscreen-loader">
        <GetLoading />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <ShareAltOutlined style={{ fontSize: 24, color: "#1890ff" }} />
          <span style={{ ...khmerFont, fontSize: 18, fontWeight: 600, marginLeft: 8 }}>
            រចនាសម្ព័ន្ធអង្គភាព
          </span>
          {stats && (
            <div style={styles.stats}>
              <Tag color="blue">{stats.total_departments} ផ្នែក</Tag>
              <Tag color="green">{stats.active_count} Active</Tag>
              <Tag color="orange">កម្រិត {stats.max_level}</Tag>
            </div>
          )}
        </div>
        <Space>
          {hasPendingChanges && (
            <>
              <Badge count={pendingChanges.length} offset={[-10, 0]}>
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  onClick={applyPendingChanges}
                  loading={saving}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                >
                  Apply Changes
                </Button>
              </Badge>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={discardPendingChanges}
              >
                Discard
              </Button>
            </>
          )}
          <Tooltip title="ធ្វើឱ្យស្រស់">
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} />
          </Tooltip>
        </Space>
      </div>

      {/* Organisation Tree */}
      <Card style={styles.treeCard}>
        <div style={styles.treeContainer}>
          {treeData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={khmerFont}>មិនមានទិន្នន័យ / No data</span>
              }
            />
          ) : (
            <DirectoryTree
              showLine={{ showLeafIcon: false }}
              expandedKeys={expandedKeys}
              selectedKeys={selectedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              onSelect={onSelect}
              treeData={treeData}
              style={styles.tree}
              draggable={false}
            />
          )}
        </div>
      </Card>

      {/* Add Sub-department Modal */}
      <Modal
        title="បន្ថែមផ្នែករង"
        open={subModalVisible}
        onCancel={() => setSubModalVisible(false)}
        onOk={handleAddSubDepartment}
        okText="រក្សាទុក"
        cancelText="បោះបង់"
        width={500}
      >
        <div style={styles.modalContent}>
          <div style={styles.formGroup}>
            <label style={khmerFont}>លេខកូដផ្នែក / Code</label>
            <Input
              value={newDepartment.code}
              disabled
              style={{ background: "#f5f5f5" }}
              placeholder="នឹងបង្កើតដោយស្វ័យប្រវត្តិ"
            />
            <small style={{ color: "#999" }}>កូដនឹងត្រូវបង្កើតដោយស្វ័យប្រវត្តិ</small>
          </div>
          <div style={styles.formGroup}>
            <label style={khmerFont}>ឈ្មោះផ្នែក / Department Name (Khmer) *</label>
            <Input
              value={newDepartment.name}
              onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
              placeholder="បញ្ចូលឈ្មោះផ្នែកជាភាសាខ្មែរ"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={englishFont}>Department Name (English)</label>
            <Input
              value={newDepartment.name_en}
              onChange={(e) => setNewDepartment({ ...newDepartment, name_en: e.target.value })}
              placeholder="Enter department name in English"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={khmerFont}>កំរិត / Level</label>
            <Input
              type="number"
              value={newDepartment.level_no}
              min={1}
              max={10}
              disabled
              style={{ background: "#f5f5f5" }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={khmerFont}>ស្ថិតក្រោម / Parent</label>
            <Input value={selectedNode?.department_name} disabled style={{ background: "#f5f5f5" }} />
          </div>
        </div>
      </Modal>

      {/* Edit Department Modal */}
      <Modal
        title="កែប្រែផ្នែក"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleUpdateDepartment}
        okText="រក្សាទុក"
        cancelText="បោះបង់"
        width={550}
      >
        {editDepartment && (
          <div style={styles.modalContent}>
            <div style={styles.formGroup}>
              <label style={khmerFont}>លេខកូដផ្នែក / Code</label>
              <Input value={editDepartment.code} disabled />
            </div>
            <div style={styles.formGroup}>
              <label style={khmerFont}>ឈ្មោះផ្នែក / Department Name (Khmer)</label>
              <Input
                value={editDepartment.department_name}
                onChange={(e) => setEditDepartment({ ...editDepartment, department_name: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={englishFont}>Department Name (English)</label>
              <Input
                value={editDepartment.department_en}
                onChange={(e) => setEditDepartment({ ...editDepartment, department_en: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={khmerFont}>កំរិត / Level</label>
              <Input
                type="number"
                value={editDepartment.level_no}
                onChange={(e) => setEditDepartment({ ...editDepartment, level_no: parseInt(e.target.value) || 1 })}
                min={1}
                max={10}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={mixedFont}>លំដាប់ / Order</label>
              <Input
                type="number"
                value={editDepartment.range_id}
                onChange={(e) => setEditDepartment({ ...editDepartment, range_id: parseInt(e.target.value) || null })}
                min={1}
                placeholder="បញ្ចូលលេខលំដាប់"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={khmerFont}>ស្ថិតក្រោម / Parent Department</label>
              <Select
                value={editDepartment.parent_id}
                onChange={(value) => setEditDepartment({ ...editDepartment, parent_id: value })}
                style={{ width: "100%" }}
                allowClear
                placeholder="ជ្រើសរើសផ្នែកមេ"
                showSearch
                optionFilterProp="children"
              >
                <Option value={0}>គ្មាន / None (Top Level)</Option>
                {departments
                  .filter(d => String(d.code) !== String(editDepartment.code))
                  .map(dept => (
                    <Option key={dept.code} value={dept.code}>
                      {dept.department_name} ({dept.code})
                    </Option>
                  ))}
              </Select>
            </div>
            <div style={styles.formGroup}>
              <label style={mixedFont}>ស្ថានភាព / Status</label>
              <Select
                value={editDepartment.status}
                onChange={(value) => setEditDepartment({ ...editDepartment, status: value })}
                style={{ width: "100%" }}
              >
                <Option value={1}>Active / ដំណើរការ</Option>
                <Option value={0}>Inactive / បិទ</Option>
              </Select>
            </div>
          </div>
        )}
      </Modal>

      {/* Move Department Modal */}
      <Modal
        title="ផ្លាស់ទីផ្នែក"
        open={moveModalVisible}
        onCancel={() => setMoveModalVisible(false)}
        onOk={handleMoveDepartment}
        okText="Add to Pending"
        cancelText="បោះបង់"
        width={500}
      >
        <div style={styles.modalContent}>
          <div style={styles.formGroup}>
            <label style={khmerFont}>ផ្នែកដែលត្រូវផ្លាស់ទី</label>
            <Input value={selectedNode?.department_name} disabled style={{ background: "#f5f5f5" }} />
          </div>
          <div style={styles.formGroup}>
            <label style={khmerFont}>ផ្លាស់ទីទៅកាន់</label>
            <Select
              value={moveTarget}
              onChange={setMoveTarget}
              style={{ width: "100%" }}
              allowClear
              placeholder="ជ្រើសរើសផ្នែកមេថ្មី"
              showSearch
              optionFilterProp="children"
            >
              <Option value={0}>កម្រិតកំពូល / Top Level</Option>
              {departments
                .filter(d => String(d.code) !== String(selectedNode?.code))
                .map(dept => (
                  <Option key={dept.code} value={dept.code}>
                    {dept.department_name} ({dept.code})
                  </Option>
                ))}
            </Select>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: "#fff7e6", borderRadius: 8 }}>
            <small>Note: This change will be added to pending queue. Click "Apply Changes" to save to server.</small>
          </div>
        </div>
      </Modal>


      {/* Delete Department Modal */}
      <DeleteDepartmentModal
        visible={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDepartmentToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        departmentName={departmentToDelete?.department_name}
        departmentCode={departmentToDelete?.code}
        parentDept={departmentToDelete?.parent_dept}
        loading={deleteLoading}
      />

      {/* Staff List Modal */}
      <DepartmentStaffModal
        visible={staffModalVisible}
        onCancel={() => {
          setStaffModalVisible(false);
          setSelectedDepartment(null);
        }}
        department={selectedDepartment}
        org_code={org_code}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    background: "#fbfbfbff",
    maxHeight: "calc(100vh - 200px)",


  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    padding: "0 8px",
    flexWrap: "wrap",
    gap: 12,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  stats: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  treeCard: {
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  treeContainer: {
    padding: "16px",
    maxHeight: "calc(100vh - 350px)",
    overflowY: "auto",
  },
  tree: {
    background: "transparent",
  },
  treeNodeTitle: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: "8px 0",
    width: "100%",
    borderBottom: "1px solid #f0f0f0",
  },
  treeNodeIcon: {
    width: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  treeNodeInfo: {
    flex: 1,
    minWidth: 200,
  },
  treeNodeName: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  treeNodeEn: {
    fontSize: 12,
    color: "#666",
  },
  treeNodeMeta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  metaTag: {
    fontSize: 10,
    margin: 0,
    padding: "0 6px",
    height: "auto",
    lineHeight: "18px",
  },
  treeNodeActions: {
    display: "flex",
    gap: 4,
    flexShrink: 0,
  },
  dotGreen: {
    width: 12,
    height: 12,
    borderRadius: 12,
    background: "#28a745",
    boxShadow: "0 0 6px rgba(40,167,69,0.7)",
    marginRight: 6,
  },
  dotYellow: {
    width: 12,
    height: 12,
    borderRadius: 12,
    background: "#f0ad4e",
    boxShadow: "0 0 6px rgba(240,173,78,0.8)",
    marginRight: 6,
  },
  dotGray: {
    width: 8,
    height: 8,
    borderRadius: 8,
    background: "#9e9e9e",
    marginRight: 8,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
  },
  modalContent: {
    padding: "8px 0",
  },
  formGroup: {
    marginBottom: 16,
  },
  staffContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  staffAvatar: {
    border: "2px solid #fff",
    boxShadow: "0 0 4px rgba(0,0,0,0.2)",
    backgroundColor: "#1890ff",
  },
  avatarWrapper: {
    position: "relative",
    display: "inline-block",
  },
  onlineDot: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#52c41a",
    border: "2px solid #fff",
    zIndex: 1,
  },
  offlineDot: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#d9d9d9",
    border: "2px solid #fff",
    zIndex: 1,
  },
};

export default OrganisationChart;